import { histogram } from "./measures";
import type { DemoOutcome } from "./schema";
import type { DemoEndReason } from "./vocabulary";

/**
 * How much a demo's visitor played (ADR 0016; docs/admin-app/figures.md › Demo outcomes), in four
 * tiers. The thresholds are first guesses; past outcomes keep the tier they were given.
 */

export const DEMO_TIERS = ["left_at_once", "looked_around", "tried_it", "explored"] as const;
export type DemoTier = (typeof DEMO_TIERS)[number];

/** Left at once: one visit, no change, one area at most and less active time than this. */
export const LEFT_AT_ONCE_SECONDS = 2 * 60;
/** Tried it: changes in this many areas, or this many changes. */
export const TRIED_IT_AREAS = 2;
export const TRIED_IT_CHANGES = 10;
/** Explored: changes in this many areas, and this much active time or a second visit. */
export const EXPLORED_AREAS = 3;
export const EXPLORED_SECONDS = 15 * 60;
export const EXPLORED_VISITS = 2;

export interface DemoActivity {
  visits: number;
  changes: number;
  activeSeconds: number;
  areasRead: readonly string[];
  areasChanged: readonly string[];
}

/** The tier of a demo by what its visitor did; "looked around" is everything between. */
export function demoEngagement(demo: DemoActivity): DemoTier {
  const areasChanged = demo.areasChanged.length;
  if (
    areasChanged >= EXPLORED_AREAS &&
    (demo.activeSeconds >= EXPLORED_SECONDS || demo.visits >= EXPLORED_VISITS)
  ) {
    return "explored";
  }
  if (areasChanged >= TRIED_IT_AREAS || demo.changes >= TRIED_IT_CHANGES) return "tried_it";
  if (
    demo.visits <= 1 &&
    demo.changes === 0 &&
    demo.activeSeconds < LEFT_AT_ONCE_SECONDS &&
    demo.areasRead.length <= 1
  ) {
    return "left_at_once";
  }
  return "looked_around";
}

/** What a demo collected while it lasted (`demo_garages`), read as values. */
export interface DemoRecord {
  outcomeId: string;
  requestedAt: string;
  openedAt: string | null;
  startedAt: string | null;
  language: string | null;
  exampleData: boolean | null;
  deviceType: string | null;
  resets: number;
  firstUsedAt: string | null;
  lastUsedAt: string | null;
  activeSeconds: number;
  visits: number;
  requests: number;
  changes: number;
  areasRead: readonly string[];
  areasChanged: readonly string[];
  firstAreaChanged: string | null;
}

const MINUTE_MS = 60_000;
const day = (iso: string) => new Date(iso).toISOString().slice(0, 10);
const minutesBetween = (from: string, to: string) =>
  Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / MINUTE_MS));

/**
 * The anonymous outcome a demo leaves when it ends (docs/admin-app/figures.md › Demo outcomes):
 * dates to the day, durations in minutes, and nothing that names the visitor, their garage or
 * their email.
 */
export function demoOutcome(
  demo: DemoRecord,
  endedBy: DemoEndReason,
  now: Date,
): Required<DemoOutcome> {
  const ended = now.toISOString();
  const activity = {
    visits: demo.visits,
    changes: demo.changes,
    activeSeconds: demo.activeSeconds,
    areasRead: demo.areasRead,
    areasChanged: demo.areasChanged,
  };
  return {
    id: demo.outcomeId,
    endedBy,
    endedOn: day(ended),
    requestedOn: day(demo.requestedAt),
    startedOn: demo.startedAt ? day(demo.startedAt) : null,
    language: demo.language,
    exampleData: demo.exampleData,
    deviceType: demo.deviceType,
    linkOpened: demo.openedAt !== null,
    minutesToOpen: demo.openedAt ? minutesBetween(demo.requestedAt, demo.openedAt) : null,
    welcomeDone: demo.startedAt !== null,
    lifetimeMinutes: demo.startedAt ? minutesBetween(demo.startedAt, ended) : null,
    spanMinutes:
      demo.startedAt && demo.lastUsedAt ? minutesBetween(demo.startedAt, demo.lastUsedAt) : null,
    activeSeconds: Math.round(demo.activeSeconds),
    visits: demo.visits,
    cameBack:
      demo.firstUsedAt !== null &&
      demo.lastUsedAt !== null &&
      day(demo.lastUsedAt) > day(demo.firstUsedAt),
    requests: demo.requests,
    changes: demo.changes,
    areasRead: [...demo.areasRead],
    areasChanged: [...demo.areasChanged],
    firstAreaChanged: demo.firstAreaChanged,
    resets: demo.resets,
    engagement: demoEngagement(activity),
  };
}

/** Lower edges of the demo summary's buckets: minutes to open the link, active minutes, visits, hours. */
export const DEMO_OPEN_EDGES_MINUTES = [0, 5, 15, 60, 240, 1440] as const;
export const DEMO_ACTIVE_EDGES_MINUTES = [0, 2, 5, 15, 30, 60] as const;
export const DEMO_VISIT_EDGES = [0, 1, 2, 3, 5, 10] as const;
/** Hours from start to end; a demo runs 48 hours at most. */
export const DEMO_LIFETIME_EDGES_HOURS = [0, 1, 4, 12, 24, 36, 47] as const;

/** The steps of the way in, each a subset of the one before. */
export const DEMO_STEPS = ["requested", "opened", "started", "changed", "came_back"] as const;
export type DemoStep = (typeof DEMO_STEPS)[number];

export interface DemoSummary {
  /** Demos per step of the way in. */
  steps: Record<DemoStep, number>;
  /** Per bucket of `DEMO_OPEN_EDGES_MINUTES`, over the demos whose link was opened. */
  minutesToOpen: number[];
  /** Started demos per choice and device, each with its demos per `DEMO_TIERS`. */
  byExampleData: { value: string; count: number; tiers: number[] }[];
  byLanguage: { value: string; count: number; tiers: number[] }[];
  byDevice: { value: string; count: number; tiers: number[] }[];
  /** Over the started demos, per bucket of their edges. */
  activeMinutes: number[];
  visits: number[];
  spanHours: number[];
  /** Hours from start to end of the demos the visitor ended. */
  endedByVisitorHours: number[];
  /** Demos started over, and how often in all. */
  reset: { demos: number; resets: number };
  /** Started demos per area looked at, changed, and changed first. */
  areasRead: { value: string; count: number }[];
  areasChanged: { value: string; count: number }[];
  firstAreaChanged: { value: string; count: number }[];
}

const tallied = (values: Iterable<string>) => {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
};

function byChoice(started: readonly DemoOutcome[], choice: (o: DemoOutcome) => string) {
  return tallied(started.map(choice)).map(({ value, count }) => ({
    value,
    count,
    tiers: DEMO_TIERS.map(
      (tier) => started.filter((o) => choice(o) === value && o.engagement === tier).length,
    ),
  }));
}

/**
 * The demo page's figures over a range's outcomes (docs/admin-app/figures.md › Does the demo
 * convince?): counted here so core and the page don't each fold the outcomes their own way.
 */
export function demoSummary(outcomes: readonly DemoOutcome[]): DemoSummary {
  const started = outcomes.filter((o) => o.startedOn);
  const minutes = (seconds: number | undefined) => (seconds ?? 0) / 60;
  const hours = (m: number | null | undefined) => (m ?? 0) / 60;
  return {
    steps: {
      requested: outcomes.length,
      opened: outcomes.filter((o) => o.linkOpened).length,
      started: started.length,
      changed: started.filter((o) => (o.changes ?? 0) > 0).length,
      came_back: started.filter((o) => o.cameBack).length,
    },
    minutesToOpen: histogram(
      outcomes.flatMap((o) => (o.linkOpened && o.minutesToOpen != null ? [o.minutesToOpen] : [])),
      DEMO_OPEN_EDGES_MINUTES,
    ),
    byExampleData: byChoice(started, (o) =>
      o.exampleData == null ? "unknown" : o.exampleData ? "example" : "empty",
    ),
    byLanguage: byChoice(started, (o) => o.language ?? "unknown"),
    byDevice: byChoice(started, (o) => o.deviceType ?? "unknown"),
    activeMinutes: histogram(
      started.map((o) => minutes(o.activeSeconds)),
      DEMO_ACTIVE_EDGES_MINUTES,
    ),
    visits: histogram(
      started.map((o) => o.visits ?? 0),
      DEMO_VISIT_EDGES,
    ),
    spanHours: histogram(
      started.map((o) => hours(o.spanMinutes)),
      DEMO_LIFETIME_EDGES_HOURS,
    ),
    endedByVisitorHours: histogram(
      started.filter((o) => o.endedBy === "visitor").map((o) => hours(o.lifetimeMinutes)),
      DEMO_LIFETIME_EDGES_HOURS,
    ),
    reset: {
      demos: started.filter((o) => (o.resets ?? 0) > 0).length,
      resets: started.reduce((t, o) => t + (o.resets ?? 0), 0),
    },
    areasRead: tallied(started.flatMap((o) => o.areasRead ?? [])),
    areasChanged: tallied(started.flatMap((o) => o.areasChanged ?? [])),
    firstAreaChanged: tallied(
      started.flatMap((o) => (o.firstAreaChanged ? [o.firstAreaChanged] : [])),
    ),
  };
}
